/**
 * Which tab body to render.
 *
 * The switch is its own component so the page does not carry a second one;
 * every branch is a tab that already lives in this folder. Every tab but
 * Marketing is editable exactly when the server says the caller may write the
 * venue; Marketing follows `canWriteMarketing` inside its own tab. Workshops
 * is the exception: it checks its own workshop-write permission.
 */
import type { VenueDetail, VenuePatch } from '../../../../api/venues';
import type { WorkshopShell } from '../../workshopDetailUrlFor';

import type { VenuePhotoFlow } from './photo/use-venue-photo-flow';
import { ReservedTab } from './ReservedTab';
import type { Tab } from './venue-tabs';
import { VenueDetailsTab } from './VenueDetailsTab';
import { VenueMarketingTab } from './VenueMarketingTab';
import { VenueWorkshopsTab } from './VenueWorkshopsTab';

const DELETE_BLOCKED =
  'Delete unlocks once no workshops reference this venue (count check arrives with the Workshops tab).';

export function VenueTabPanel({
  tab,
  venue,
  venueId,
  photoFlow,
  shell,
  save,
  onDelete,
}: {
  tab: Tab;
  venue: VenueDetail;
  venueId: string;
  photoFlow: VenuePhotoFlow;
  shell: WorkshopShell;
  save: (patch: VenuePatch) => Promise<void>;
  onDelete: () => Promise<void>;
}) {
  const readOnly = !venue.canEdit;
  switch (tab) {
    case 'Marketing':
      return <VenueMarketingTab venue={venue} />;
    case 'Workshops':
      return <VenueWorkshopsTab venueId={venueId} shell={shell} />;
    case 'Relationship':
      return <ReservedTab tab={tab} />;
    case 'Details':
    default:
      return (
        <VenueDetailsTab
          venue={venue}
          readOnly={readOnly}
          photoFlow={photoFlow}
          save={save}
          onDelete={onDelete}
          deleteBlockedReason={DELETE_BLOCKED}
        />
      );
  }
}
